import { isNotDefined } from '@sgme/fp';
import { isEqual } from 'lodash-es';
import type { FieldMetadata } from 'models/forms/fields/FieldMetadata';
import type { GridMetadata, GridWithoutPaginationMetadata } from 'models/forms/fields/GridMetadata';

import type { SubKeyExtractor } from '.';

const CELL_KEY_DELIMITER = '::';

type GridRow = Record<string, unknown>;

export type GridColumn<Row> = {
  id: string;
  getValue: (row: Row | undefined) => unknown;
  isVisible?: (row: Row | undefined) => boolean;
  includeOnRowChange?: boolean;
};

export type GridExtractorOptions<Row, Meta extends FieldMetadata> = {
  isHandled: (meta: FieldMetadata) => meta is Meta;
  getRows: (value: unknown) => readonly Row[];
  getColumns: (meta: Meta, previousRows: readonly Row[], currentRows: readonly Row[]) => readonly GridColumn<Row>[];
  includeParentFieldOnRowChange?: boolean;
  getRowIdentity?: (row: Row) => unknown;
};

export function buildPatchedCellKey(gridId: string, rowIndex: number, columnId: string): string {
  return `${gridId}${CELL_KEY_DELIMITER}${rowIndex}${CELL_KEY_DELIMITER}${columnId}`;
}

export function asGridRows<Row>(value: unknown): readonly Row[] {
  return Array.isArray(value) ? (value as readonly Row[]) : [];
}

export function asGridRowsOrSingle<Row>(value: unknown): readonly Row[] {
  if (Array.isArray(value)) {
    return value as readonly Row[];
  }

  return value !== undefined && value !== null ? [value as Row] : [];
}

export function isGridMetadata(meta: FieldMetadata): meta is GridMetadata | GridWithoutPaginationMetadata {
  return meta.type === 'GridWithoutPagination';
}

function isColumnVisible<Row>(column: GridColumn<Row>, row: Row | undefined): boolean {
  return column.isVisible?.(row) ?? true;
}

function getVisibleColumns<Row>(columns: readonly GridColumn<Row>[], row: Row | undefined): readonly GridColumn<Row>[] {
  return columns.filter(column => isColumnVisible(column, row));
}

function isIncludedOnRowChange<Row>(column: GridColumn<Row>, row: Row | undefined): boolean {
  return column.includeOnRowChange ?? column.getValue(row) !== undefined;
}

export function createGridCellExtractor<Row, Meta extends FieldMetadata>(
  options: GridExtractorOptions<Row, Meta>,
): SubKeyExtractor {
  return (gridId, meta, previous, current) => {
    if (!options.isHandled(meta)) {
      return [];
    }

    const previousRows = options.getRows(previous);
    const currentRows = options.getRows(current);
    const columns = options.getColumns(meta, previousRows, currentRows);
    const rowCount = Math.max(previousRows.length, currentRows.length);
    const keys: string[] = [];
    for (let rowIndex = 0; rowIndex < rowCount; rowIndex++) {
      const previousRow = previousRows[rowIndex];
      const currentRow = currentRows[rowIndex];

      if (isNotDefined(currentRow)) {
        continue;
      }

      if (isNotDefined(previousRow)) {
        const rowChangeKeyCount = keys.length;
        for (const column of getVisibleColumns(columns, currentRow)) {
          if (isIncludedOnRowChange(column, currentRow)) {
            keys.push(buildPatchedCellKey(gridId, rowIndex, column.id));
          }
        }
        if (options.includeParentFieldOnRowChange && keys.length === rowChangeKeyCount) {
          keys.push(gridId);
        }
        continue;
      }

      const rowIdentityChanged =
        options.getRowIdentity !== undefined &&
        !isEqual(options.getRowIdentity(previousRow), options.getRowIdentity(currentRow));

      for (const column of columns) {
        if (!isColumnVisible(column, previousRow) && !isColumnVisible(column, currentRow)) {
          continue;
        }

        if (!isEqual(column.getValue(previousRow), column.getValue(currentRow))) {
          keys.push(buildPatchedCellKey(gridId, rowIndex, column.id));
        }
      }

      if (options.includeParentFieldOnRowChange && rowIdentityChanged && keys.length === 0) {
        keys.push(gridId);
      }
    }

    return keys;
  };
}

export const gridCellExtractor = createGridCellExtractor<GridRow, GridMetadata | GridWithoutPaginationMetadata>({
  isHandled: isGridMetadata,
  getRows: asGridRows<GridRow>,
  getColumns: meta =>
    meta.headers.map(header => ({
      id: header.id,
      getValue: row => row?.[header.id],
    })),
});
